/**
 * User-facing messages for candidate-facing public auth flows.
 * Generic API copy lives in utils/errors.js — these add flow-specific hints.
 */
import { formatApiError, formatTokenError, isNetworkError } from './errors';

/**
 * Sign-up errors. Backend: POST /api/auth/candidate-signup
 * (400 validation, 404 no match, 409 exists).
 */
export function formatCandidateSignupError(err) {
  if (!err || isNetworkError(err)) {
    return 'Could not reach the server. Check your network. If this persists, confirm the app is built with the correct API base URL (e.g. REACT_APP_API_URL or a dev proxy to /api).';
  }
  const { status, data } = err.response;

  if (status === 409) {
    return data?.error || 'An account already exists for this email. Please sign in instead.';
  }
  if (status === 404) {
    return data?.error
      || 'No application matches this email and application code. Use the exact email you applied with and your HIQ- code (same format as in your confirmation).';
  }
  if (status === 400 && Array.isArray(data?.errors) && data.errors.length) {
    const parts = data.errors.map(e => (typeof e === 'string' ? e : e.msg || e.message)).filter(Boolean);
    return parts.join(' ') || data?.error || 'Please check the form and try again.';
  }
  return formatApiError(err, 'Sign up failed. Please try again.');
}

/**
 * Errors from the emailed verification link.
 * Backend: POST /api/auth/candidate-verify-signup with { token, password }.
 */
export function formatCandidateVerifySignupError(err) {
  return formatTokenError(err, 'verification');
}

/** Candidate sign-in errors. Backend: POST /api/auth/candidate-login */
export function formatCandidateLoginError(err) {
  const status = err?.response?.status;
  if (status === 401 || status === 400) {
    return err.response.data?.error
      || 'Incorrect email or password. If you have not verified your email yet, use the link we sent you.';
  }
  if (status === 403) {
    return err.response.data?.error
      || 'This account is not active yet. Check your inbox for the verification email.';
  }
  return formatApiError(err, 'Sign in failed. Please try again.');
}

/** Candidate password reset link errors. */
export function formatCandidateResetError(err) {
  return formatTokenError(err, 'reset');
}

/** Track application lookup errors (POST /api/candidates/track). */
export function formatTrackLookupError(err) {
  if (!err || isNetworkError(err)) {
    return 'Could not reach the server. Check your connection and API URL configuration.';
  }
  const { status, data } = err.response;
  if (status === 404) {
    return data?.error || 'Application not found. Check your HIQ- code and the email you used when applying (must match exactly).';
  }
  return formatApiError(err);
}
